import { Coordinate, IGeoAddress } from "common/types";
import { DEFAULT_LAT, DEFAULT_LONG, MAP_BOUNDARY_OFFSET } from "common/constants/mapbox";

const POSTAL_CODE_SUFFIX_SEPARATOR = "-";

const addPostalCode = (existingZipcode: string, postalCode: string): string => {
  const trimmedPostalCode = postalCode.trim();
  const trimmedExistingZipcode = existingZipcode.trim();
  if (!trimmedExistingZipcode || trimmedPostalCode.includes(POSTAL_CODE_SUFFIX_SEPARATOR)) {
    return trimmedPostalCode;
  }

  return `${trimmedPostalCode}${POSTAL_CODE_SUFFIX_SEPARATOR}${trimmedExistingZipcode}`;
};

const addPostalCodeSuffix = (existingZipcode: string, postalCodeSuffix: string): string => {
  const trimmedExistingZipcode = existingZipcode.trim();
  const trimmedPostalCodeSuffix = postalCodeSuffix.trim();
  if (!trimmedExistingZipcode) {
    return trimmedPostalCodeSuffix;
  }
  if (!trimmedPostalCodeSuffix || trimmedExistingZipcode.includes(POSTAL_CODE_SUFFIX_SEPARATOR)) {
    return trimmedExistingZipcode;
  }

  return `${trimmedExistingZipcode}${POSTAL_CODE_SUFFIX_SEPARATOR}${trimmedPostalCodeSuffix}`;
};

export const googlePlaceToAddress = (place: google.maps.places.PlaceResult): IGeoAddress =>
  (place.address_components || []).reduce(
    (acc, component) => {
      const address = { ...acc };

      // Grab the first type in the list since multiple can exist for a component
      const type = component.types[0];

      switch (type) {
        case "street_number": // The address number
          address.address_one = `${component.long_name} ${address.address_one}`.trim();
          break;
        case "route": // The rest of the street
          address.address_one = `${address.address_one} ${component.short_name}`.trim();
          break;
        case "postal_code": // Main part of the zipcode
          address.zipcode = addPostalCode(address.zipcode, component.long_name);
          break;
        case "postal_code_suffix": // Extra bits for the post office
          address.zipcode = addPostalCodeSuffix(address.zipcode, component.long_name);
          break;
        case "locality": // Most cities
        case "sublocality_level_1": // NYC, the special child that doesn't use locality
          address.city = component.long_name;
          break;
        case "administrative_area_level_1": // For the US, this is the state
          address.state = component.short_name;
          break;
        case "administrative_area_level_2": // County
          address.county = component.long_name;
          break;
        case "subpremise":
          address.address_two = component.long_name;
          break;
        case "country":
          address.country = component.short_name;
          break;
        default:
          break;
      }

      return address;
    },
    {
      address_one: "",
      address_two: "",
      city: "",
      state: "",
      county: "",
      zipcode: "",
      country: "",
    },
  );

/** Returns a set of coordinates defining the bounds of a viewable map, order is W, S, E, N */
export const getMapBounds = (coordinates: Coordinate[]): mapboxgl.LngLatBoundsLike => {
  // Fall back to centering around VC office if no coords are provided
  if (!coordinates.length) {
    coordinates.push([DEFAULT_LONG, DEFAULT_LAT]);
  }

  const longitudes = new Set(coordinates.map((pair) => pair[0]));
  const latitudes = new Set(coordinates.map((pair) => pair[1]));

  // Add some extra padding around the boundaries for the markers
  return [
    Math.min(...longitudes) - MAP_BOUNDARY_OFFSET,
    Math.min(...latitudes) - MAP_BOUNDARY_OFFSET,
    Math.max(...longitudes) + MAP_BOUNDARY_OFFSET,
    Math.max(...latitudes) + MAP_BOUNDARY_OFFSET,
  ];
};
